<template>
  <b-popover
    :id="`${title.toLowerCase()}-popover`"
    ref="popover"
    :target="`${title}-popover-button-${user.id}`"
    placement="top"
    triggers="click blur"
    custom-class="card__popover"
  >
    <template #title>
      {{ `Search ${title}` }}
    </template>
    <b-input
      v-model="searchValue"
      :placeholder="`Type to search ${title}`"
      debounce="300"
    >
      <b-button>
        <font-awesome-icon :icon="['fas', 'close']" />
      </b-button>
    </b-input>
    <b-spinner
      v-if="isLoading"
      class="spinner"
    />
    <recycle-scroller
      v-if="renderData && !isLoading"
      v-slot="{ item }"
      class="scroller"
      :items="renderData"
      :item-size="28"
      :key-field="currentIdKey"
    >
      <div
        class="list__item"
        @click="addNewPermission(item)"
      >
        <b-tooltip
          v-if="isTooltipVisible(item)"
          :title="item.label"
          triggers="hover"
          placement="top"
          :target="itemId(item)"
        />
        <span 
          :id="itemId(item)" 
          v-html="formattedTitle(item.label)" 
        />
      </div>
    </recycle-scroller>
  </b-popover>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
import {highlightText} from '@/core/utils';

const {mapState: sidebarMapState} = createNamespacedHelpers('sidebar');
const {mapActions: dataManagementMapActions} = createNamespacedHelpers('dataManagement');
export default {
  name: 'PermissionCardPopover',
  props: [
    'title',
    'user',
    'type',
    'localPermissions',
    'isItemWasDeleted',
    'currentIdKey',
    'currentData',
  ],
  data() {
    return {
      isLoading: false,
      searchValue: '',
      itemsToRender: null,
      copy: null,
      data: null,
    };
  },
  watch: {
    isItemWasDeleted: {
      handler() {
        const elements = Object.values(this[this.type]).filter(el => (
          !(el[this.currentIdKey] in this.currentData)
        ));
        this.copy = elements;
        this.data = elements;
      },
    },
  },
  computed: {
    ...sidebarMapState({
      tables: state => state.tablesList,
      folders: state => state.foldersList,
      modules: state => state.modulesList,
    }),
    renderData() {
      if (this.searchValue.length) {
        return this.copy.filter(el => el.label.toLowerCase().includes(this.searchValue.toLowerCase()));
      }
      return this.data;
    },
  },
  methods: {
    ...dataManagementMapActions(['createPermission', 'updatePermission']),
    formattedTitle(label) {
      return highlightText(this.searchValue, label);
    },
    /**
     * Method for determining whether to display a tooltip. 
     * If the width of the text inside is larger than the available space, the tooltip will be visible
     * @param {Object} item 
     * @returns {boolean}
     */
    isTooltipVisible(item){
      const element = document.getElementById(this.itemId(item));
      return element && (element.scrollWidth > element.offsetWidth);     
    },
    /**
     * Method for determining the ‘id’ of the items in the list to render. 
     * Depending on the type, the Item object contains unique identifiers ‘entityGuid’, ‘folderGuid’ and ‘moduleGuid’
     * @param {Object} item - Item object to be assigned 
     * @returns {string}
     */
    itemId(item){
      return item.entityGuid || item.folderGuid || item.moduleGuid;
    },
    async addNewPermission(item) {
      this.isLoading = true;

      const isPermissionExist = this.localPermissions.find(el => el.objectGuid === item[this.currentIdKey]);
      const template = {
        objectGuid: item[this.currentIdKey],
        permissionLevel: 'read',
      };
      const {body} = isPermissionExist
        ? await this.updatePermission({...isPermissionExist, ...template})
        : await this.createPermission({userGuid: this.user.id, ...template});

      this.copy = this.copy.filter(el => el[this.currentIdKey] !== item[this.currentIdKey]);
      this.data = this.copy.filter(el => el[this.currentIdKey] !== item[this.currentIdKey]);

      this.$emit('add-to-permissions', {
        item: {...item, currentUserPermissionLevel: body.permissionLevel},
        newItem: body,
      });

      this.isLoading = false;
      this.$root.$emit('bv::hide::popover');
    },
  },
  mounted() {
    const elements = Object.values(this[this.type]).filter(el => (
      !(el[this.currentIdKey] in this.currentData)
    ));
    this.copy = elements;
    this.data = elements;
  },
};
</script>

<style lang="scss">
.card__popover {
  width: 500px;
  height: 400px;
  overflow: hidden;

  & .popover-body {
    height: 100%;
    position: relative;
  }

  & .spinner {
    position: absolute;
    top: 45%;
    left: 45%;
  }

  .scroller {
    height: 300px;
  }

  .form-control {
    width: 95%;
    margin: 10px auto;
  }

  .list__item {
    padding: 7px 10px;
    border: none;
    border-bottom: 1px solid #eee;
    transition: 0.3s;
    cursor: pointer;

    span{
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      width: 500px;
    }

    &:hover {
      background-color: #eee;
    }
  }
}
</style>